import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AssetCondition,
  AssetTag,
  ClosedownFile,
  ClosedownFileType,
  ClosedownJobDetail,
  ClosedownMode,
  ClosedownOption,
  ClosedownPayload,
  ClosedownStatus,
  EquipmentStatus,
  JobUpgrade,
  PurchaseOrder,
  RootCause,
  StatusAffirmation
} from '@app/models/closedown-detail.model';
import { InvoiceService } from '@app/services/invoice/invoice.service';
import { JobService } from '@app/services/job/job.service';
import { CommonFileStorageService } from '@app/services/commonfilestorage/commonfilestorage.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { CustomValidators } from '@app/shared/custom-validators';


import { AuthService } from '@app/services/auth/auth.service';
import { Company } from '@app/models/user.model';
import { catchError, exhaustMap, firstValueFrom, forkJoin, Observable, of, Subscription, switchMap, timer } from 'rxjs';
import { COMS365ReferenceType, COMSIIEventType, FileUploadTypeCode, ClosedownFileTypeKey, JobType, Source, UserType } from '@app/shared/enums';
import { ConfigurationService } from '@app/services/configuration/configuration.service';
import { LocalizationService } from '../internationalization/localization.service';
import { SiteApiService } from '@app/services/site/site.api.service';
import { FileNote, FileStorageInfo, FileUploadRequest } from '@app/models/commonfilestorage.model';
import { UtilsService } from '@app/services/util/util-service';
import { UserService } from '@app/services/user/user.service';
import { HostListener } from '@angular/core';
import { PartnerConfiguration } from '@app/models/partnerconfiguration.model';

type DurationControlName = 'maintenanceTime' | 'travelTime';
type TimePickerControlName = DurationControlName | 'onsiteTime' | 'returnTime';

@Component({
  selector: 'app-open-job-detail',
  templateUrl: './open-job-detail.component.html',
  styleUrls: ['./open-job-detail.component.scss'],
  standalone: false,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: DateAdapter, useClass: NativeDateAdapter, deps: [MAT_DATE_LOCALE] }
  ]
})
export class OpenJobDetailComponent implements OnInit, OnDestroy {
  readonly durationFields: readonly { control: DurationControlName; label: string }[] = [
    { control: 'maintenanceTime', label: 'open_job_detail_maintenance_time' },
    { control: 'travelTime', label: 'open_job_detail_travel_time' }
  ];
  readonly durationMinuteInterval = 5;
  readonly mode: ClosedownMode = this.resolveMode();
  readonly isTechnician = this.mode === 'technician';
  readonly isApproval = this.mode === 'approval';
  readonly isAdmin = this.mode === 'admin';
  readonly updateState = signal<'pending' | 'success'>('pending');
  readonly updateCheckFailed = signal(false);
  private updatePolling?: Subscription;
  private readonly defaultUpdatePollingInterval = 10_000;
  private updatePollingInterval = this.defaultUpdatePollingInterval;

  private readonly technicianAuth = this.isTechnician ? inject(AuthService) : null;
  private readonly destroyRef = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private technicianCompanies: readonly Company[] = [];
  readonly technicianAllocationType = signal<'internal' | 'external' | null>(null);
  readonly technicianAllocationForm = this.formBuilder.nonNullable.group({
    email: [{ value: '', disabled: true }],
    companyName: [{ value: '', disabled: true }],
    technicianName: [{ value: '', disabled: true }],
    phoneNumber: [{ value: '', disabled: true }]
  });
  get jobTypeKey(): string {
    const keys: Record<string, string> = {
      NEW: 'open_jobs_summary_new',
      'ON-SITE': 'open_jobs_summary_on_site',
      RETURNING: 'open_jobs_summary_returning',
      'AWAITING-PARTS': 'open_jobs_summary_awaiting_parts',
      PPM: 'open_jobs_summary_ppm'
    };
    return keys[this.jobType.toUpperCase()] || 'open_jobs_summary_title';
  }

  applyTime(controlName: TimePickerControlName, value: string): void {
    const control = this.closedownForm.controls[controlName];
    control.setValue(value);
    control.markAsDirty();
    control.markAsTouched();
  }

  setOnsiteDateToToday(): void {
    const control = this.closedownForm.controls.onsiteDate;
    control.setValue(new Date());
    control.markAsDirty();
    control.markAsTouched();
  }

  setOnsiteTimeToNow(): void {
    const control = this.closedownForm.controls.onsiteTime;
    control.setValue(this.formatTime(new Date(), this.durationMinuteInterval));
    control.markAsDirty();
    control.markAsTouched();
  }

  setReturnDateToToday(): void {
    const control = this.closedownForm.controls.returnDate;
    control.setValue(new Date());
    control.markAsDirty();
    control.markAsTouched();
  }

  setReturnTimeToNow(): void {
    const control = this.closedownForm.controls.returnTime;
    control.setValue(this.formatTime(new Date(), this.durationMinuteInterval));
    control.markAsDirty();
    control.markAsTouched();
  }

  readonly closedownForm = this.formBuilder.group({
    assetTag: [''],
    rootCause: [null as string | null, Validators.required],
    gasUsed: [false],
    gasType: [null as string | null],
    gasUsage: [0, Validators.min(0)],
    gasLeakType: [null as string | null],
    status: [null as string | null, Validators.required],
    statusSubType: [null as string | null, Validators.required],
    isRaisedByPPM: [null as boolean | null, Validators.required],
    assetOnDeparture: [null as string | null],
    equipmentStatus: [null as string | null],
    notes: [''],
    onsiteDate: [new Date() as Date | null, Validators.required],
    onsiteTime: ['00:00', Validators.required],
    maintenanceTime: ['00:00', [Validators.required, CustomValidators.isTimeValid()]],
    travelTime: ['00:00', [Validators.required, CustomValidators.isTimeValid()]],
    returnDate: [null as Date | null],
    returnTime: ['']
  });

  job?: ClosedownJobDetail;
  latestClosedown: ClosedownPayload | null = null;
  rootCauses: RootCause[] = [];
  statuses: ClosedownStatus[] = [];
  affirmations: StatusAffirmation[] = [];
  gasTypes: ClosedownOption[] = [];
  gasLeakTypes: ClosedownOption[] = [];
  equipmentStatuses: EquipmentStatus[] = [];
  assetConditions: AssetCondition[] = [];
  purchaseOrder: PurchaseOrder | null = null;
  upgrades: JobUpgrade[] = [];
  photoReasonsDisplay: string[] = [];
  photoReasons: FileNote[] = [];
  assetTagDetail: AssetTag | null = null;
  files: ClosedownFile[] = [];
  readonly maxOnsiteDate = new Date();
  readonly minReturnDate = new Date();
  photosAllowFileTypes: string[] = [];
  photoMaxSize: number = 0;
  maxSupportingDocuments: number = 0;
  maxServiceSheets: number = 0;
  fileErrorKey: string = '';  
  fileErrorParams: Record<string, string | number> = {};
  siteId: string = '';
  siteName: string = '';
  jobType: string = '';
  jobId: string = '';
  isSaving:boolean = false;
  hasLoadError: boolean = false;
  loadErrorKey: string = '';
  saveErrorKey: string = '';
  saveErrorMessage: string = '';
  saveSucceeded: boolean = false;

  // Document upload settings
  readonly allowedImageTypes = ['image/jpeg'];
  readonly reactiveJobType = 'REACTIVE';
  enableReactiveJobPhotos = false;
  enablePPMJobPhotos = false;
  jobPhotoMaxCount = 0;
  jobPhotoMaxSizeInBytes = 0;
  enableReactiveJobSupportingDocuments = false;
  enablePPMJobSupportingDocuments = false;
  noOfEnablePPMJobSupportingDocumentsAllowed = 0;
  noOfEnableReactiveJobSupportingDocumentsAllowed = 0;
  supportingDocumentAllowFileTypes: string[] = [];
  supportingDocumentAllowMaxSizeInMBs = 0;
  enableReactiveJobServiceSheets = false;
  enablePPMJobServiceSheets = false;
  noOfEnableReactiveJobServiceSheetsAllowed = 0;
  noOfEnablePPMJobServiceSheetsAllowed = 0;
  serviceSheetAllowFileTypes: string[] = [];
  serviceSheetAllowMaxSizeInMBs = 0;
  invoiceFilePrefix: string[] = []; 
  allowedSupportingDocumentFileTypesDisplay: string = '';
  allowedServiceSheetFileTypesDisplay: string = '';
  invoiceFilePrefixDisplay: string = '';
  partnerReferenceNumber: string = '';
  partnerId: string = '';
  partnerConfiguration: PartnerConfiguration | null = null;

  // Document upload
  uploadedJobFiles: FileStorageInfo[] = [];
  public selectedPhoto: FileStorageInfo | null = null;
  public isLoadingJobPhotos = false;
  jobPurchaseOrder: PurchaseOrder | null = null;
  
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly jobService: JobService,
    private readonly _invoiceService: InvoiceService,
    private readonly navigationService: NavigationService,
    private readonly _commonFileStorageService: CommonFileStorageService,
    private _configurationService: ConfigurationService,
    private _localizationService: LocalizationService,
    private _siteApiService: SiteApiService,
    private _utilsService: UtilsService
  ) {
    this.navigationService.onChange(false, false);
  }

  get isAssetTagNotAvailable(): boolean {
    return this.closedownForm.controls.assetTag.hasError('assetTagNotAvailable');
  }

  get showGasFields(): boolean {
    return this.closedownForm.controls.gasUsed.value ?? false;
  }

  get showGasSection(): boolean {
    return this.isAdmin
      || this.gasLeakTypes.length > 0
      || (this.isApproval && !!this.latestClosedown?.wasGasUsed);
  }

  get pendingJobListType(): 'New' | 'PPM' {
    return this.jobType.toUpperCase() === JobType.PPM_JOB ? 'PPM' : 'New';
  }

  get showReturnEta(): boolean {
    const statusId = this.closedownForm.controls.status.value;
    const statusName = this.statuses.find(status => status.id === statusId)?.name
      ?.replaceAll('-', '').replaceAll(' ', '').toUpperCase();
    return statusName === 'RETURNING' || statusName === 'AWAITINGPARTS';
  }

  get photos(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'photo');
  }

  get supportingDocuments(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'supporting-document');
  }

  get serviceSheets(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'service-sheet');
  }

  get canCreateUplift(): boolean {
    return !!this.purchaseOrder?.id
      && this.purchaseOrder.upliftRequests.length === 0
      && this.purchaseOrder.uplifts.length === 0;
  }

  async ngOnInit(): Promise<void> {
    this.siteId = this.route.snapshot.paramMap.get('siteId') || '';
    this.jobType = this.route.snapshot.paramMap.get('jobType') || '';
    this.jobId = this.route.snapshot.paramMap.get('jobId') || '';
    this.siteName = this.route.snapshot.queryParamMap.get('siteName') || '';
    if (this.isApproval) {
      this.siteId = this.route.snapshot.queryParamMap.get('siteId') || '';
      this.jobType = this.route.snapshot.queryParamMap.get('jobType') || '';
    }
    if (this.technicianAuth) {
      try {
        const [user, roles] = await Promise.all([
          this.technicianAuth.getUser(),
          this.technicianAuth.getUserRoles()
        ]);
        const isInternal = roles.includes(UserType.INTERNAL_USER);
        const isExternal = roles.includes(UserType.EXTERNAL_USER);
        if (!user?.emailAddress || (!isInternal && !isExternal)) {
          this.setLoadError('open_job_detail_allocation_load_error');
          return;
        }
        this.technicianAllocationType.set(isInternal ? 'internal' : 'external');
        this.technicianCompanies = user.companies ?? [];
        this.technicianAllocationForm.patchValue({
          email: user.emailAddress,
          technicianName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
          phoneNumber: user.mobileNumber ?? ''
        });
      } catch {
        this.setLoadError('open_job_detail_allocation_load_error');
        return;
      }
    }

    let shouldRetrieveFromAPI = await this._configurationService.shouldRetrieveFromAPI();
    if(shouldRetrieveFromAPI){
      await this._configurationService.getAndSetLocalConfigurations(true);
    }

    await this.populateJobConfigurationSettings();

    if (
      !this.jobId ||
      (this.isAdmin && (!this.siteId || !this.jobType))
    ) {
      this.setLoadError('open_job_detail_error_missing_details');
      return;
    }

    if (!this.isAdmin) {
      this.closedownForm.controls.gasUsed.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(gasUsed => {
        this.updateGasValidators(gasUsed ?? false);
      });
    }

    this.closedownForm.controls.status.valueChanges.pipe(
      switchMap(statusId => statusId === null
        ? of([] as StatusAffirmation[])
        : this.jobService.getAffirmations(statusId))
    ).subscribe({
      next: affirmations => {
        this.affirmations = affirmations;
        this.closedownForm.controls.statusSubType.setValue(this.affirmations[0]?.id ?? null);
        this.updateReturnEtaValidation();

        if (!this.showSupportingDocuments()) {
          this.files = this.files.filter(
            file => file.type !== ClosedownFileTypeKey.SUPPORTING_DOCUMENT
          );
        }

        if (!this.showServiceSheets()) {
          this.files = this.files.filter(
            file => file.type !== ClosedownFileTypeKey.SERVICE_SHEET
          );
        }
      },
      error: () => {
        this.affirmations = [];
        this.closedownForm.controls.statusSubType.setValue(null);
        this.saveErrorKey = 'open_job_detail_error_status_options';
      }
    });

    if (this.isApproval) {
      this.loadPendingApproval();
      return;
    }

    const jobRequest = this.isTechnician
      ? this.jobService.getAllocatedJobById(this.jobId)
      : this.jobService.getJob(this.jobId);

    jobRequest.pipe(
      switchMap(job => {
        if (!job) {
          if (this.isAdmin) {
            return this.jobService.getLatestClosedownByJobId(this.jobId).pipe(
              switchMap(closedown => {
                if (
                  closedown?.jobId.toLowerCase() === this.jobId.toLowerCase() &&
                  this.isClosedownUpdated(closedown)
                ) {
                  this.saveSucceeded = true;
                  this.setClosedownUpdateState(closedown);
                } else {
                  this.setLoadError('open_job_detail_error_no_job');
                }
                return of(null);
              })
            );
          }
          this.setLoadError(this.isTechnician
            ? 'open_job_detail_allocated_job_unavailable'
            : 'open_job_detail_error_no_job');
          return of(null);
        }
        if (job.jobEventClosedown) {
          this.job = job;
          if (this.isTechnician) {
            this.siteId = job.siteId;
            this.jobType = job.jobType;
          }
          this.siteName = this.siteName || job.site?.name || '';
          this.trackClosedownUpdate(job.jobEventClosedown);
          return of(null);
        }
        return forkJoin({
          job: of(job),
          rootCauses: this.jobService.getRootCauses(
            job.brandId || job.site?.brand?.id || '',
            job.disciplineReferenceId || '',
            job.faultServiceReferenceId || '',
            job.incidentTypeReferenceId || ''
          ),
          statuses: this.jobService.getStatuses(),
          purchaseOrder: this.isTechnician ? of(null) : this._invoiceService.getUpliftPurchaseOrder(this.jobId, job.companyId).pipe(
            catchError(() => of(null))
          ),
          gasTypes: this.jobService.getGasTypes(),
          gasLeakTypes: this.jobService.getGasLeakTypes(),
          equipmentStatus: this.jobService.getEquipmentStatuses(),
          assetConditions: this.jobService.getAssetConditions()
        });
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: result => {
        if (!result) {
          return;
        }
        this.job = result.job;
        if (this.isTechnician && this.technicianAllocationType() === 'external') {
          const company = this.technicianCompanies.find(item => item.id === result.job.allocateCompanyId);
          this.technicianAllocationForm.controls.companyName.setValue(company?.name ?? '');
        }
        if (this.isTechnician) {
          this.siteId = result.job.siteId;
          this.jobType = result.job.jobType;
        }
        this.siteName = this.siteName || result.job.site?.name || '';
        this.rootCauses = result.rootCauses || [];
        this.statuses = result.statuses || [];
        this.gasTypes = result.gasTypes || [];
        this.gasLeakTypes = this.isAdmin
          ? result.gasLeakTypes
          : result.gasLeakTypes.filter(option =>
            !!result.job.disciplineReferenceId &&
            option.disciplineReferenceId === result.job.disciplineReferenceId);
        this.equipmentStatuses = result.equipmentStatus || [];
        this.assetConditions = result.assetConditions || [];
        this.purchaseOrder = result.purchaseOrder;
        this.upgrades  = [];
        this.closedownForm.patchValue({ assetTag: result.job.asset?.assetTag || '' });
        this.setOnsiteToNow();

        this._siteApiService.getBrandByReferenceNumber(this.job.site.brand.referenceNumber ?? '').subscribe({
          next: async brand => {
            this.partnerReferenceNumber = brand?.partner?.referenceNumber ?? '';
            this.partnerId = brand?.partner?.id ?? '';

            this._commonFileStorageService.GetPhotoUploadReasons(this.partnerReferenceNumber).subscribe({
              next: (photoUploadReasons) => {
                this.photoReasons = photoUploadReasons || [];
                this.photoReasonsDisplay = this.photoReasons.map(reason => reason.description);
              },
              error: (error) => {
                this.setLoadError('open_job_detail_error_load_photo_reasons');
              }
            });

            try {
              this.partnerConfiguration = await firstValueFrom(this._siteApiService.getPartnerConfigurationByPartnerId(this.partnerId));
            } catch (error) {
              this.setLoadError('open_job_detail_error_load_partner_configuration');
            }

            this.isLoadingJobPhotos = true;
            try {
              this.uploadedJobFiles = await firstValueFrom(this.getJobFiles());
            } catch (error) {
              this.setLoadError('open_job_detail_error_load_job_files');
            } finally {
              this.isLoadingJobPhotos = false;
            }
          },
          error: () => {
            this.isLoadingJobPhotos = false;
            this.setLoadError('open_job_detail_error_load_details');
          }
        });

        if(this.job?.source === Source.COMS365)
        {
          this._invoiceService.getPurchaseOrdersByJobIdAndCompanyId(this.job.id, this.job.companyId).subscribe({
            next: purchaseOrders => {
                this.jobPurchaseOrder = purchaseOrders[0];
            },
            error: error => {
                console.error(error);
            }
          });
        }
      },
      error: () => this.setLoadError('open_job_detail_error_load_details')
    });
  }

  ngOnDestroy(): void {
    this.updatePolling?.unsubscribe();
    this.navigationService.onChange(true, false);
    document.body.style.overflow = '';
  }

  setOnsiteToNow(): void {
    const now = new Date();
    this.closedownForm.patchValue({
      onsiteDate: now,
      onsiteTime: this.formatTime(now, this.durationMinuteInterval)
    });
  }

  isReactiveJob(): boolean {
    return this.job?.jobType.toUpperCase() === this.reactiveJobType;
  }

  removeFile(fileToRemove: ClosedownFile): void {
    this.files = this.files.filter(file => file !== fileToRemove);
  }

  clearError(): void {
    this.saveErrorKey = '';
    this.saveErrorMessage = '';
  }

  goBack(): void {
    if (this.isTechnician) {
      void this.router.navigate(['/siteinductioncompleted']);
      return;
    }
    if (this.isApproval) {
      void this.router.navigate(['/pendingapprovaljob/list'], {
        queryParams: {
          siteId: this.siteId,
          siteName: this.siteName,
          jobType: this.pendingJobListType
        }
      });
      return;
    }
    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType],
      { queryParams: { siteName: this.siteName } }
    );
  }

  openUpgrade(): void {
    if (!this.isAdmin) {
      return;
    }
    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType, this.jobId, 'upgrade'],
      { queryParams: { siteName: this.siteName } }
    );
  }

  async saveClosedown(): Promise<void> {
    if (this.isSaving || this.saveSucceeded) {
      return;
    }
    this.saveErrorKey = '';
    this.saveErrorMessage = '';
    this.saveSucceeded = false;
    this.closedownForm.markAllAsTouched();

    if (this.closedownForm.invalid || (!this.job && !this.isApproval)) {
      return;
    }

    if (this.isApproval && !this.latestClosedown?.id) {
      this.saveErrorKey = 'open_job_detail_error_pending_closedown_missing';
      return;
    }

    if (!this.isAdmin && (
      this.toMinutes(this.closedownForm.controls.maintenanceTime.value!) <= 0 ||
      this.toMinutes(this.closedownForm.controls.travelTime.value!) <= 0
    )) {
      this.saveErrorKey = 'open_job_detail_error_positive_duration';
      return;
    }

    const onsite = this.toDate(
      this.closedownForm.controls.onsiteDate.value!,
      this.closedownForm.controls.onsiteTime.value!
    );
    const returnEta = this.showReturnEta
      ? this.toDate(
        this.closedownForm.controls.returnDate.value!,
        this.closedownForm.controls.returnTime.value!
      )
      : undefined;

    if (!onsite || this.addDuration(onsite, this.closedownForm.controls.maintenanceTime.value!) > new Date()) {
      this.saveErrorKey = 'open_job_detail_error_future_job_time';
      return;
    }

    if (this.isOnsiteBeforeScheduled(onsite)) {
      this.saveErrorKey = 'open_job_detail_error_onsite_before_scheduled';
      return;
    }

    if (this.showReturnEta && (!returnEta || returnEta <= new Date())) {
      this.saveErrorKey = 'open_job_detail_error_future_eta';
      return;
    }

    if (this.photos.some(photo => !photo.reason)) {
      this.saveErrorKey = 'open_job_detail_error_photo_reason';
      return;
    }

    if (!this.isApproval && !this.validateRequiredDocuments()) {
      return;
    }

    this.isSaving = true;

    const uploadedFiles: FileStorageInfo[] = [];
    try {
      try {
        await this.uploadFiles(this.photos, uploadedFiles);
      } catch (error) {
        this.saveErrorKey = 'open_job_detail_error_photo_upload';
        throw error;
      }

      try {
        await this.uploadFiles(this.supportingDocuments, uploadedFiles);
      } catch (error) {
        this.saveErrorKey = 'open_job_detail_error_supporting_document_upload';
        throw error;
      }

      try {
        await this.uploadFiles(this.serviceSheets, uploadedFiles);
      } catch (error) {
        this.saveErrorKey = 'open_job_detail_error_service_sheet_upload';
        throw error;
      }

      const signInSignOutId = (await this.userService.getSignIn())?.signInSignOutId;
      const payload = this.buildPayload(onsite, returnEta, signInSignOutId);
      let result: Pick<ClosedownPayload, 'jobId' | 'messageReceived' | 'isActive'>;
      if (this.isApproval) {
        result = await firstValueFrom(this.jobService.approveClosedown(payload));
      } else if (this.isTechnician) {
        result = await firstValueFrom(this.jobService.saveTechnicianClosedown(payload));
      } else {
        result = await firstValueFrom(this.jobService.saveClosedown(payload));
      }
      this.saveSucceeded = true;
      this.trackClosedownUpdate(result, true);
    } catch (error) {
      if(uploadedFiles.length > 0) {
        await this.deleteUploadedFiles(uploadedFiles);
      }

      if (!this.saveErrorKey) {
        this.saveErrorKey = 'open_job_detail_error_save';
      }

      this.saveErrorMessage = error instanceof HttpErrorResponse ? (error.error?.errorMessage ?? '') : '';
    } finally {
      this.isSaving = false;
    }
  }

  private trackClosedownUpdate(
    closedown: Pick<ClosedownPayload, 'jobId' | 'messageReceived' | 'isActive'>,
    forcePending = false
  ): void {
    this.updatePolling?.unsubscribe();
    this.updateCheckFailed.set(false);
    if (forcePending) {
      this.updateState.set('pending');
    } else {
      this.setClosedownUpdateState(closedown);
    }
    if (this.updateState() === 'success' || this.destroyRef.destroyed) {
      return;
    }

    this.updatePolling = timer(this.updatePollingInterval, this.updatePollingInterval).pipe(
      exhaustMap(() =>
        this.jobService.getLatestClosedownByJobId(this.jobId).pipe(
          catchError(() => {
            this.updateCheckFailed.set(true);
            return of(undefined);
          })
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(latestClosedown => {
      if (latestClosedown === undefined) {
        return;
      }
      if (latestClosedown === null) {
        this.updateCheckFailed.set(false);
        return;
      }
      if (latestClosedown.jobId.toLowerCase() !== this.jobId.toLowerCase()) {
        this.updateCheckFailed.set(true);
        return;
      }
      this.updateCheckFailed.set(false);
      this.setClosedownUpdateState(latestClosedown);
      if (this.updateState() === 'success') {
        this.updatePolling?.unsubscribe();
      }
    });
  }

  private setClosedownUpdateState(
    closedown: Pick<ClosedownPayload, 'jobId' | 'messageReceived' | 'isActive'>
  ): void {
    this.updateState.set(this.isClosedownUpdated(closedown) ? 'success' : 'pending');
  }

  private isClosedownUpdated(
    closedown: Pick<ClosedownPayload, 'jobId' | 'messageReceived' | 'isActive'>
  ): boolean {
    const isActive = closedown.isActive ?? false;
    return !!closedown.messageReceived && (isActive === true || isActive === 1);
  }

  private async uploadFiles(files: ClosedownFile[], uploadedFiles: FileStorageInfo[]): Promise<void> {
    for (const file of files) {
      const fileRequest = await this.toFileUploadRequest(file);

      const result = await firstValueFrom(this._commonFileStorageService.UploadFile(fileRequest, file.file, this.partnerReferenceNumber));

      uploadedFiles.push(result);
    }
  }

  // #region Document upload

  protected enableJobPhotos(): boolean {
    if(this.isReactiveJob()) 
    {
      return this.enableReactiveJobPhotos;
    }
    else 
    {
      return this.enablePPMJobPhotos;
    }
  }

  protected enableJobSupportingDocuments(): boolean {
    if(this.isReactiveJob()) 
    {
      return this.enableReactiveJobSupportingDocuments;
    }
    else 
    {
      return this.enablePPMJobSupportingDocuments;
    }
  }

  protected enableJobServiceSheets(): boolean {
    if(this.isReactiveJob()) 
    {
      return this.partnerConfiguration?.enableReactiveJobServiceSheets ?? false;
    }
    else 
    {
      return this.partnerConfiguration?.enablePPMJobServiceSheets ?? false;
    }
  }

  private isStatusAllowed(configValue: string | null | undefined): boolean {
    if (!configValue?.trim()) {
      return true;
    }

    const selectedStatusId = this.closedownForm.controls.status.value;
    if (!selectedStatusId) {
      return false;
    }

    const selectedStatusName = this.statuses.find(x => x.id === selectedStatusId)?.name;
    if (!selectedStatusName) {
      return false;
    }

    const normalize = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]/g, '');


    return configValue
      .split(',')
      .map(status => normalize(status))
      .includes(normalize(selectedStatusName));
  }

  protected showSupportingDocuments(): boolean {
    if (!this.enableJobSupportingDocuments()) {
      return false;
    }

    const allowStatuses = this.isReactiveJob()
      ? this.partnerConfiguration?.reactiveSupportingDocumentAllowStatus
      : this.partnerConfiguration?.ppmSupportingDocumentAllowStatus;

    return this.isStatusAllowed(allowStatuses);
  }

  protected showServiceSheets(): boolean {
    if (!this.enableJobServiceSheets()) {
      return false;
    }

    const allowStatuses = this.isReactiveJob()
      ? this.partnerConfiguration?.reactiveJobServiceSheetsAllowStatus
      : this.partnerConfiguration?.ppmJobServiceSheetsAllowStatus;

    return this.isStatusAllowed(allowStatuses);
  }

  async populateJobConfigurationSettings(): Promise<void> {
    this.enableReactiveJobPhotos = await this._configurationService.getJobEnableReactiveJobPhotos();
    this.enablePPMJobPhotos = await this._configurationService.getJobEnablePPMJobPhotos();
    this.enableReactiveJobSupportingDocuments = await this._configurationService.getJobEnableReactiveJobSupportingDocuments();
    this.enablePPMJobSupportingDocuments = await this._configurationService.getJobEnablePPMJobSupportingDocuments();
    this.jobPhotoMaxCount = await this._configurationService.getJobPhotoMaxCount();
    this.jobPhotoMaxSizeInBytes = await this._configurationService.getJobPhotoMaxSizeInBytes();
    this.noOfEnablePPMJobSupportingDocumentsAllowed = await this._configurationService.getJobNoOfEnablePPMJobSupportingDocumentsAllowed();
    this.noOfEnableReactiveJobSupportingDocumentsAllowed = await this._configurationService.getJobNoOfEnableReactiveJobSupportingDocumentsAllowed();
    this.supportingDocumentAllowFileTypes = await this._configurationService.getJobSupportingDocumentAllowFileTypes();
    this.supportingDocumentAllowMaxSizeInMBs = await this._configurationService.getJobSupportingDocumentAllowMaxSizeInMBs();
    this.noOfEnableReactiveJobServiceSheetsAllowed = await this._configurationService.getJobNoOfEnableReactiveJobServiceSheetsAllowed();
    this.noOfEnablePPMJobServiceSheetsAllowed = await this._configurationService.getJobNoOfEnablePPMJobServiceSheetsAllowed();
    this.serviceSheetAllowFileTypes = await this._configurationService.getJobServiceSheetAllowFileTypes();
    this.serviceSheetAllowMaxSizeInMBs = await this._configurationService.getJobServiceSheetAllowMaxSizeInMBs();
    this.invoiceFilePrefix = await this._configurationService.getJobInvoiceFilePrefix();

    const pollingIntervalInSeconds = await this._configurationService.getJobClosedownPollingIntervalInSeconds();
    this.updatePollingInterval = Number.isFinite(pollingIntervalInSeconds) && pollingIntervalInSeconds > 0
      ? pollingIntervalInSeconds * 1000
      : this.defaultUpdatePollingInterval;

    this.allowedSupportingDocumentFileTypesDisplay = this.supportingDocumentAllowFileTypes
      .map(type => type.split('/')[1])
      .join(', ');
    this.allowedServiceSheetFileTypesDisplay = this.serviceSheetAllowFileTypes
      .map(type => type.split('/')[1])
      .join(', ');
      this.invoiceFilePrefixDisplay = this.invoiceFilePrefix
      .map(prefix => prefix)
      .join(', ');
  }

  public selectFiles(event: Event, type: ClosedownFileType): void {
    const input = event.target as HTMLInputElement;

    for (const file of Array.from(input.files || [])) {
      if (!this.validateFile(file, type)) {
        continue;
      }

      this.files.push({
        file,
        type,
        reason: type === ClosedownFileTypeKey.PHOTO ? this.photoReasonsDisplay[0] || '' : undefined
      });
    }

    input.value = '';
  }

  private validateFile(file: File, type: ClosedownFileType): boolean {
    const fileAlreadyUploaded = this.files.some(
      existingFile => existingFile.file.name.toLowerCase() === file.name.toLowerCase()
    );

    if (fileAlreadyUploaded) {
      alert(this._localizationService.translate('open_job_detail_error_file_already_uploaded').replace('{fileName}', file.name));
      return false;
    }

    switch (type) {
      case ClosedownFileTypeKey.PHOTO: {
        return this.validatePhoto(file);
      }
        
      case ClosedownFileTypeKey.SUPPORTING_DOCUMENT: {
        return this.validateSupportingDocument(file);
      }

      case ClosedownFileTypeKey.SERVICE_SHEET: {
        return this.validateServiceSheet(file);
      }

      default:
        return false;
    }
  }

  private validatePhoto(file: File): boolean {
    const isValidImage = this.allowedImageTypes.includes(file.type);

    if (file.size > this.jobPhotoMaxSizeInBytes) {
      const maxSizeInMB = (this.jobPhotoMaxSizeInBytes / 1024 / 1024).toFixed(0);
      alert(this._localizationService.translate('open_job_detail_error_max_photo_size').replace('{jobPhotoMaxSize}', `${maxSizeInMB}`));

      return false;
    }
    else if (!isValidImage) {
      alert(this._localizationService.translate('open_job_detail_error_invalid_photo_type'));

      return false;
    }

    return true;
  }

  private validateSupportingDocument(file: File): boolean {
    const maxSizeInMB = this.supportingDocumentAllowMaxSizeInMBs;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      alert(this._localizationService.translate('open_job_detail_error_max_supporting_document_size').replace('{supportingDocumentMaxSize}', `${maxSizeInMB}`));

      return false;
    }
    else if (this.supportingDocumentAllowFileTypes.length > 0 && !this.supportingDocumentAllowFileTypes.includes(file.type)) {
      alert(this._localizationService.translate('open_job_detail_error_invalid_supporting_document_type').replace('{fileName}', file.name).replace('{allowedSupportingDocumentFileTypes}', this.allowedSupportingDocumentFileTypesDisplay));

      return false;
    }
    else if (this.invoiceFilePrefix.some(prefix => file.name.toUpperCase().startsWith(prefix))) {
      alert(this._localizationService.translate('open_job_detail_error_invoice_prefix_not_allowed').replace('{invoicePrefix}', this.invoiceFilePrefixDisplay));

      return false;
    }

    return true;
  }

  private validateServiceSheet(file: File): boolean {
    const maxSizeInMB = this.serviceSheetAllowMaxSizeInMBs;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      alert(this._localizationService.translate('open_job_detail_error_max_service_sheet_size').replace('{serviceSheetMaxSize}', `${maxSizeInMB}`));
      
      return false;
    }
    else if (this.serviceSheetAllowFileTypes.length > 0 && !this.serviceSheetAllowFileTypes.includes(file.type)){
      alert(this._localizationService.translate('open_job_detail_error_invalid_service_sheet_type').replace('{fileName}', file.name).replace('{allowedServiceSheetFileTypes}', this.allowedServiceSheetFileTypesDisplay));

      return false;
    }
    else if (this.invoiceFilePrefix.some(prefix => file.name.toUpperCase().startsWith(prefix))) {
      alert(this._localizationService.translate('open_job_detail_error_invoice_prefix_not_allowed').replace('{invoicePrefix}', this.invoiceFilePrefixDisplay));

      return false;
    }

    return true;
  }

  public onAddFileClick(event: MouseEvent, type: ClosedownFileType): void {
    if (!this.canAddFile(type)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private canAddFile(type: ClosedownFileType): boolean {
    switch (type) {
      case ClosedownFileTypeKey.PHOTO: {
        return this.canAddPhoto();
      }

      case ClosedownFileTypeKey.SUPPORTING_DOCUMENT: {
        return this.canAddSupportingDocument();
      }

      case ClosedownFileTypeKey.SERVICE_SHEET: {
        return this.canAddServiceSheet();
      }

      default:
        return false;
    }
  }

  private canAddPhoto(): boolean {
    if (this.photos.length >= this.jobPhotoMaxCount) {
      const errorMessage = this._localizationService.translate('open_job_detail_error_max_photos').replace('{jobPhotoMaxCount}', `${this.jobPhotoMaxCount}`);
      alert(errorMessage);

      return false;
    }

    return true;
  }

  private canAddSupportingDocument(): boolean {
    const maxCount = this.isReactiveJob() ? this.noOfEnableReactiveJobSupportingDocumentsAllowed : this.noOfEnablePPMJobSupportingDocumentsAllowed;

    if (this.supportingDocuments.length >= maxCount) {
      const errorMessage = this._localizationService.translate('open_job_detail_error_max_supporting_document_count').replace('{supportingDocumentMaxCount}', `${maxCount}`);
      alert(errorMessage);

      return false;
    }

    return true;
  }

  private canAddServiceSheet(): boolean {
    const maxCount = this.isReactiveJob() ? this.noOfEnableReactiveJobServiceSheetsAllowed : this.noOfEnablePPMJobServiceSheetsAllowed;

    if (this.serviceSheets.length >= maxCount) {
      const errorMessage = this._localizationService.translate('open_job_detail_error_max_service_sheet_count').replace('{serviceSheetMaxCount}', `${maxCount}`);
      alert(errorMessage);

      return false;
    }

    return true;
  }

  private getJobFiles(): Observable<FileStorageInfo[]> {
    let referenceType: string;
    const includeBase64String = true;

    if (this.isReactiveJob()) {
      referenceType = 'JobEvent';
    } else {
      referenceType = 'PPMScheduleLineEvent';
    }

    return this._commonFileStorageService.GetFileStorageInfosByReferenceTypeAndIds(
      referenceType,
      this.job?.calloutReferenceNumber ?? '',
      this.partnerReferenceNumber,
      includeBase64String
    );
  }

  private async deleteUploadedFiles(uploadedFiles: FileStorageInfo[]): Promise<void> {
    if (uploadedFiles.length > 0) {
        for (const uploadedFile of uploadedFiles) {
          try {
            await firstValueFrom(
              this._commonFileStorageService.DeleteFileById(
                uploadedFile.id,
                this.partnerReferenceNumber
              )
            );
          } catch (error) {
          }
        }
      }
  }

  private async toFileUploadRequest(item: ClosedownFile): Promise<FileUploadRequest> {
    let fileUploadTypeCode: string;
    let fileNoteId: string | undefined;
    let referenceType: string;
    let referenceId: string;
    let thumbnail: string | undefined;

    referenceType = this.isReactiveJob() ? COMSIIEventType.JOBEVENT : COMSIIEventType.PPMSCHEDULELINEEVENT;
    referenceId = this.job?.calloutReferenceNumber ?? '';

    if (item.type === ClosedownFileTypeKey.PHOTO) {
      fileUploadTypeCode = FileUploadTypeCode.PHOTO;
      thumbnail = await this._utilsService.createThumbnail(item.file);
    } 
    else if (item.type === ClosedownFileTypeKey.SUPPORTING_DOCUMENT) {
      fileUploadTypeCode = FileUploadTypeCode.SUPPORTING_DOCUMENT;
    } 
    else {
      fileUploadTypeCode = FileUploadTypeCode.SERVICE_SHEET;
    }

    if (item.reason) {
      fileNoteId = this.photoReasons.find(x => x.description === item.reason)?.id;
    }

    if (this.job?.source === Source.COMS365) {
      if (item.type === ClosedownFileTypeKey.SERVICE_SHEET) {
        referenceType = COMS365ReferenceType.PURCHASE_ORDER;
        referenceId = `${this.jobPurchaseOrder?.purchaseOrderNumber}-${this.jobPurchaseOrder?.legalEntity}`;
      } 
      // Photo and Supporting Documents
      else {
        referenceType = COMS365ReferenceType.BOOKING;
      }
    }

    return {
      fileName: item.file.name,
      fileUploadTypeCode: fileUploadTypeCode,
      fileExtension: '.' + item.file.name.split('.').pop()?.toLowerCase(),
      fileContentType: item.file.type,
      referenceType: referenceType,
      referenceId: referenceId,
      siteId: this.job?.site.id,
      brandId: this.job?.site.brand.id,
      caption: item.file.name,
      fileNoteId,
      note: item.reason,
      thumbnail: thumbnail
    };
  }

  private validateRequiredDocuments(): boolean {
    const isSupportingDocumentsOptional = this.isReactiveJob()
      ? this.partnerConfiguration?.isReactiveJobSupportingDocumentsOptional
      : this.partnerConfiguration?.isPPMJobSupportingDocumentsOptional;

    const isServiceSheetsOptional = this.isReactiveJob()
      ? this.partnerConfiguration?.isReactiveJobServiceSheetsOptional
      : this.partnerConfiguration?.isPPMJobServiceSheetsOptional;

    const jobType = this.isReactiveJob() ? 'Reactive' : 'PPM';

    if (!isSupportingDocumentsOptional && this.supportingDocuments.length <= 0) {
      alert(this._localizationService.translate('open_job_detail_error_supporting_document_required').replace('{jobType}', jobType));
      this.saveErrorKey = '';
      return false;
    }

    if (!isServiceSheetsOptional && this.serviceSheets.length <= 0) {
      alert(this._localizationService.translate('open_job_detail_error_service_sheet_required').replace('{jobType}', jobType));
      this.saveErrorKey = '';
      return false;
    }

    return true;
  }

  // #endregion Document upload

  // #region Photo display

  protected get uploadedJobPhotos(): FileStorageInfo[] {
    return this.uploadedJobFiles.filter(file => file.fileUploadTypeCode === FileUploadTypeCode.PHOTO);
  }

  public openPhoto(photo: FileStorageInfo): void {
    this.selectedPhoto = photo;
    document.body.style.overflow = 'hidden';
  }

  public closePhoto(): void {
    this.selectedPhoto = null;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  public onEscapePressed(): void {
    if (this.selectedPhoto) {
      this.closePhoto();
    }
  }

  // #endregion Photo display

  private updateReturnEtaValidation(): void {
    const returnDate = this.closedownForm.controls.returnDate;
    const returnTime = this.closedownForm.controls.returnTime;

    if (this.showReturnEta) {
      returnDate.addValidators(Validators.required);
      returnTime.addValidators(Validators.required);

      const now = new Date();
      returnDate.setValue(now);
      returnTime.setValue(this.formatTime(now));
    } else {
      returnDate.clearValidators();
      returnTime.clearValidators();
      returnDate.setValue(null);
      returnTime.setValue('');
    }

    returnDate.updateValueAndValidity({ emitEvent: false });
    returnTime.updateValueAndValidity({ emitEvent: false });
  }

  lookupAssetTag(assetTag: string, siteId: string): void {
    const assetTagControl = this.closedownForm.controls.assetTag;
    const trimmedAssetTag = assetTag.trim();

    if (!trimmedAssetTag || !siteId || assetTagControl.invalid) {
      return;
    }

    this.jobService.getAssetTag(siteId, trimmedAssetTag).subscribe({
      next: asset => {
        if (asset?.assetTag) {
          this.assetTagDetail = asset;
        } else {
          this.setAssetTagNotAvailable();
        }
      },
      error: () => this.setAssetTagNotAvailable()
    });
  }

  private setAssetTagNotAvailable(): void {
    const assetTagControl = this.closedownForm.controls.assetTag;
    assetTagControl.setErrors({ ...assetTagControl.errors, assetTagNotAvailable: true });
    assetTagControl.markAsTouched();
  }

  private isOnsiteBeforeScheduled(onsite: Date): boolean {
    const events = this.job?.jobEvents ??
      (this.job?.latestJobEvent ? [this.job.latestJobEvent] : []);
    const latestScheduledTime = events.reduce<number | null>((latest, event) => {
      if (event.bookingStatus?.toLowerCase() !== 'scheduled' || !event.bookedOnUtc) {
        return latest;
      }

      const bookedOnTime = new Date(event.bookedOnUtc).getTime();
      if (Number.isNaN(bookedOnTime)) {
        return latest;
      }

      return latest === null || bookedOnTime > latest ? bookedOnTime : latest;
    }, null);

    return latestScheduledTime !== null && onsite.getTime() < latestScheduledTime;
  }

  private buildPayload(onsite: Date, returnEta?: Date, signInSignOutId?: string): ClosedownPayload {
    const values = this.closedownForm.getRawValue();
    const wasGasUsed = this.showGasSection && (values.gasUsed ?? false);
    const pending = this.isApproval ? this.latestClosedown : null;

    return {
      ...pending,
      id: pending?.id ?? undefined,
      jobId: this.jobId,
      jobEventId: pending?.jobEventId ?? this.job?.latestJobEvent?.id ?? '',
      assetId: this.isTechnician
        ? this.job?.asset?.assetId ?? null
        : this.assetTagDetail?.id ?? pending?.assetId ?? null,
      signInSignOutId: pending?.signInSignOutId ?? signInSignOutId ?? null,
      closedownStatusId: values.status!,
      statusAffirmationId: values.statusSubType!,
      equipmentStatusId: values.equipmentStatus,
      assetConditionId: values.assetOnDeparture,
      rootCauseId: values.rootCause!,
      onsiteDateTime: onsite.toISOString(),
      revisedETA: returnEta?.toISOString() ?? null,
      notes: values.notes || null,
      timeSpentInMinutes: this.toMinutes(values.maintenanceTime!),
      travelTimeInMinutes: this.toMinutes(values.travelTime!),
      wasGasUsed,
      gasTypeId: wasGasUsed ? values.gasType : null,
      gasUsageKg: wasGasUsed ? values.gasUsage ?? null : null,
      gasLeakTypeId: wasGasUsed ? values.gasLeakType : null,
      wasThirdPartyUsed: pending?.wasThirdPartyUsed ?? false,
      thirdPartyReason: pending?.thirdPartyReason ?? null,
      isRaisedByPPM: values.isRaisedByPPM!,
      latLon: pending?.latLon ?? null,
      isPendingApproval: pending ? pending.isPendingApproval ?? false : this.isTechnician
    };
  }

  private toMinutes(duration: string): number {
    const [hours, minutes] = duration.split(':').map(Number);
    return (hours * 60) + minutes;
  }

  private toDate(date: Date | null, time: string): Date | undefined {
    if (!date || !time) {
      return undefined;
    }

    const [hours, minutes] = time.split(':').map(Number);
    const value = new Date(date);
    value.setHours(hours, minutes, 0, 0);
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  private addDuration(start: Date, duration: string): Date {
    const [hours, minutes] = duration.split(':').map(Number);
    return new Date(start.getTime() + ((hours * 60) + minutes) * 60_000);
  }

  private formatTime(value: Date, minuteInterval = 1): string {
    const minutes = Math.floor(value.getMinutes() / minuteInterval) * minuteInterval;
    return `${String(value.getHours()).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private formatDuration(totalMinutes: number): string {
    const minutes = Math.max(0, Math.trunc(totalMinutes));
    const hours = Math.floor(minutes / 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  }

  private updateGasValidators(gasUsed: boolean): void {
    const controls = this.closedownForm.controls;
    controls.gasType.setValidators(gasUsed ? [Validators.required] : []);
    controls.gasLeakType.setValidators(gasUsed ? [Validators.required] : []);
    controls.gasUsage.setValidators(gasUsed
      ? [Validators.required, Validators.min(0)]
      : [Validators.min(0)]);
    controls.gasType.updateValueAndValidity({ emitEvent: false });
    controls.gasLeakType.updateValueAndValidity({ emitEvent: false });
    controls.gasUsage.updateValueAndValidity({ emitEvent: false });
  }

  private setLoadError(messageKey: string): void {
    this.hasLoadError = true;
    this.loadErrorKey = messageKey;
  }

  private resolveMode(): ClosedownMode {
    const routeMode = this.route.snapshot.data['closedownMode'];
    return routeMode === 'technician' || routeMode === 'approval' ? routeMode : 'admin';
  }

  // Pending approval jobs are loaded from the saved closedown only; the job lookup returns nothing for them.
  private loadPendingApproval(): void {
    this.jobService.getLatestClosedownByJobId(this.jobId).pipe(
      switchMap(closedown => {
        if (!this.isPendingClosedown(closedown)) {
          this.setLoadError('open_job_detail_error_no_job');
          return of(null);
        }
        return forkJoin({
          closedown: of(closedown),
          affirmations: this.jobService.getAffirmations(closedown.closedownStatusId),
          statuses: this.jobService.getStatuses(),
          gasTypes: this.jobService.getGasTypes(),
          gasLeakTypes: this.jobService.getGasLeakTypes(),
          equipmentStatus: this.jobService.getEquipmentStatuses(),
          assetConditions: this.jobService.getAssetConditions()
        });
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: result => {
        if (!result) {
          return;
        }
        this.latestClosedown = result.closedown;
        // The root cause list needs job details that are not available, so only the saved value is offered.
        this.rootCauses = [{ id: result.closedown.rootCauseId, name: result.closedown.rootCauseId }] as RootCause[];
        this.affirmations = result.affirmations ?? [];
        this.statuses = result.statuses || [];
        this.gasTypes = result.gasTypes || [];
        this.gasLeakTypes = result.gasLeakTypes || [];
        this.equipmentStatuses = result.equipmentStatus || [];
        this.assetConditions = result.assetConditions || [];
        this.populateFromClosedown(result.closedown);
      },
      error: () => this.setLoadError('open_job_detail_error_load_details')
    });
  }

  private isPendingClosedown(closedown: ClosedownPayload | null): closedown is ClosedownPayload {
    return !!closedown?.id && closedown.jobId.toLowerCase() === this.jobId.toLowerCase();
  }

  private populateFromClosedown(closedown: ClosedownPayload): void {
    const onsiteDate = new Date(closedown.onsiteDateTime);
    const returnDate = closedown.revisedETA ? new Date(closedown.revisedETA) : null;
    if (Number.isNaN(onsiteDate.getTime()) || (returnDate && Number.isNaN(returnDate.getTime()))) {
      this.setLoadError('open_job_detail_error_load_details');
      return;
    }

    this.closedownForm.patchValue({
      rootCause: closedown.rootCauseId,
      gasUsed: closedown.wasGasUsed,
      gasType: closedown.gasTypeId ?? null,
      gasUsage: closedown.gasUsageKg ?? 0,
      gasLeakType: closedown.gasLeakTypeId ?? null,
      status: closedown.closedownStatusId,
      statusSubType: closedown.statusAffirmationId,
      assetOnDeparture: closedown.assetConditionId ?? null,
      equipmentStatus: closedown.equipmentStatusId ?? null,
      isRaisedByPPM: closedown.isRaisedByPPM,
      notes: closedown.notes ?? '',
      onsiteDate,
      onsiteTime: this.formatTime(onsiteDate),
      maintenanceTime: this.formatDuration(closedown.timeSpentInMinutes),
      travelTime: this.formatDuration(closedown.travelTimeInMinutes)
    }, { emitEvent: false });
    this.updateGasValidators(closedown.wasGasUsed);
    this.updateReturnEtaValidation();
    if (this.showReturnEta && returnDate) {
      this.closedownForm.patchValue({
        returnDate,
        returnTime: this.formatTime(returnDate)
      }, { emitEvent: false });
    }
  }
}